import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Pencil, Trash2, Plus, Search, MapPin, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import { Checkbox } from "@/components/ui/checkbox";
import { API } from "@/lib/api";
import { ITALIAN_PROVINCES, ITALIAN_CITIES, COUNTRIES } from "@/data/italianData";
import useSort from "@/lib/useSort";
import { useHasPerm } from "@/components/PermissionGate";

const EMPTY = {
  ragione_sociale: "", nome: "", cognome: "", indirizzo: "",
  email: "", telefono: "", civico: "", cap: "", provincia: "",
  paese: "Italia", citta: "", pec: "", codice_fiscale: "", partita_iva: "",
  lat: null, lng: null,
};

export default function Fornitori() {
  const hasPerm = useHasPerm();
  const canCreate = hasPerm("fornitori:create");
  const canEdit = hasPerm("fornitori:edit");
  const canDelete = hasPerm("fornitori:delete");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [search, setSearch] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [importBusy, setImportBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/suppliers");
      setList(data);
    } catch (e) {
      toast.error("Errore caricamento fornitori");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setForm({ ...EMPTY, ...s });
    setDialogOpen(true);
  };

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.ragione_sociale || !form.nome || !form.email || !form.telefono) {
      toast.error("Compila i campi obbligatori");
      return;
    }
    try {
      if (editing) {
        await api.put(`/suppliers/${editing.id}`, form);
        toast.success("Fornitore aggiornato");
      } else {
        await api.post("/suppliers", form);
        toast.success("Fornitore creato");
      }
      setDialogOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore salvataggio");
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/suppliers/${deleteId}`);
      toast.success("Fornitore eliminato");
      setDeleteId(null);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const bulkDelete = async () => {
    try {
      const { data } = await api.post("/suppliers/bulk-delete", { ids: Array.from(selectedIds) });
      toast.success(`${data.deleted} fornitori eliminati`);
      setSelectedIds(new Set());
      setBulkDeleteOpen(false);
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const downloadTemplate = async () => {
    try {
      const token = localStorage.getItem("ds_token");
      const res = await fetch(`${API}/suppliers/template`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      if (!res.ok) throw new Error("Errore download");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = "template_fornitori.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) { toast.error(err.message); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImportBusy(true); setImportResult(null);
    try {
      const fd = new FormData(); fd.append("file", file);
      const token = localStorage.getItem("ds_token");
      const res = await fetch(`${API}/suppliers/import`, {
        method: "POST", body: fd,
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      setImportResult(data);
      toast.success(`${data.created} fornitori importati`);
      if (data.skipped?.length) toast.warning(`${data.skipped.length} righe saltate`);
      await load();
    } catch (err) {
      toast.error(typeof err.message === "string" ? err.message : "Errore import");
    } finally {
      setImportBusy(false); e.target.value = "";
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleSelectAll = (rows) => {
    setSelectedIds((prev) => {
      if (rows.every((r) => prev.has(r.id))) return new Set();
      return new Set(rows.map((r) => r.id));
    });
  };

  const filtered = useMemo(() => {
    if (!search.trim()) return list;
    const q = search.toLowerCase();
    return list.filter((s) =>
      Object.values(s).some((v) => v && String(v).toLowerCase().includes(q))
    );
  }, [list, search]);

  const { toggleSort, SortIcon, sortedRows: sortedFiltered } = useSort(filtered, {
    initial: { by: "ragione_sociale", dir: "asc" },
    getKey: (row, by) => {
      if (by === "referente") return `${row.nome || ""} ${row.cognome || ""}`.trim();
      return row[by];
    },
  });

  return (
    <div className="space-y-6" data-testid="fornitori-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <div className="label-tag mb-2">Anagrafiche</div>
          <h1 className="font-brand text-4xl sm:text-5xl font-light tracking-tight">Fornitori</h1>
          <p className="text-sm mt-2" style={{ color: "var(--ds-text-secondary)" }}>
            Gestisci i tuoi partner commerciali.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {selectedIds.size > 0 && canDelete && (
            <Button
              onClick={() => setBulkDeleteOpen(true)}
              variant="outline"
              className="rounded-lg"
              style={{ borderColor: "var(--ds-danger)", color: "var(--ds-danger)" }}
              data-testid="fornitori-bulk-delete-button"
            >
              <Trash2 size={14} className="mr-1.5" /> Elimina {selectedIds.size}
            </Button>
          )}
          <Button onClick={downloadTemplate} variant="outline" className="rounded-lg" data-testid="fornitori-template-button">
            <Download size={14} className="mr-1.5" /> Template XLS
          </Button>
          {canCreate && (
            <Button onClick={() => { setImportOpen(true); setImportResult(null); }} variant="outline" className="rounded-lg" data-testid="fornitori-import-button">
              <Upload size={14} className="mr-1.5" /> Importa XLS
            </Button>
          )}
          {canCreate && (
            <Button onClick={openCreate} className="ds-btn-primary" data-testid="fornitore-add-button">
              <Plus size={16} className="mr-1.5" /> Aggiungi fornitore
            </Button>
          )}
        </div>
      </div>

      <div className="ds-card p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--ds-text-secondary)" }} />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Cerca per qualsiasi campo…"
              className="pl-9 rounded-lg"
              data-testid="fornitori-search"
            />
          </div>
          <div className="text-sm" style={{ color: "var(--ds-text-secondary)" }}>
            {filtered.length} risultati
          </div>
        </div>

        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={sortedFiltered.length > 0 && sortedFiltered.every((r) => selectedIds.has(r.id))}
                    onCheckedChange={() => toggleSelectAll(sortedFiltered)}
                    data-testid="fornitori-select-all"
                  />
                </TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("ragione_sociale")} data-testid="sort-forn-ragione">Ragione sociale<SortIcon col="ragione_sociale" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("referente")} data-testid="sort-forn-referente">Referente<SortIcon col="referente" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("email")} data-testid="sort-forn-email">Email<SortIcon col="email" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("telefono")} data-testid="sort-forn-telefono">Telefono<SortIcon col="telefono" /></TableHead>
                <TableHead className="cursor-pointer select-none" onClick={() => toggleSort("citta")} data-testid="sort-forn-citta">Città<SortIcon col="citta" /></TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-sm" style={{ color: "var(--ds-text-secondary)" }}>Caricamento…</TableCell></TableRow>
              )}
              {!loading && sortedFiltered.length === 0 && (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-sm" style={{ color: "var(--ds-text-secondary)" }}>Nessun fornitore trovato</TableCell></TableRow>
              )}
              {sortedFiltered.map((s) => (
                <TableRow key={s.id} data-testid={`fornitore-row-${s.id}`}>
                  <TableCell>
                    <Checkbox
                      checked={selectedIds.has(s.id)}
                      onCheckedChange={() => toggleSelect(s.id)}
                      data-testid={`fornitore-select-${s.id}`}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{s.ragione_sociale}</TableCell>
                  <TableCell>{s.nome} {s.cognome}</TableCell>
                  <TableCell className="text-sm">{s.email}</TableCell>
                  <TableCell className="text-sm">{s.telefono}</TableCell>
                  <TableCell className="text-sm">{s.citta} {s.provincia ? `(${s.provincia})` : ""}</TableCell>
                  <TableCell className="text-right">
                    {canEdit && (
                      <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`fornitore-edit-${s.id}`}>
                        <Pencil size={14} />
                      </Button>
                    )}
                    {canDelete && (
                      <Button size="sm" variant="ghost" onClick={() => setDeleteId(s.id)} data-testid={`fornitore-delete-${s.id}`}>
                        <Trash2 size={14} style={{ color: "var(--ds-danger)" }} />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="fornitore-dialog">
          <DialogHeader>
            <DialogTitle className="font-brand text-3xl font-light">
              {editing ? "Modifica fornitore" : "Nuovo fornitore"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            <div className="md:col-span-2">
              <Label className="label-tag">Ragione sociale *</Label>
              <Input value={form.ragione_sociale} onChange={(e) => set("ragione_sociale", e.target.value)} className="mt-1.5 rounded-lg" required data-testid="form-ragione-sociale" />
            </div>
            <div>
              <Label className="label-tag">Nome *</Label>
              <Input value={form.nome} onChange={(e) => set("nome", e.target.value)} className="mt-1.5 rounded-lg" required data-testid="form-nome" />
            </div>
            <div>
              <Label className="label-tag">Cognome</Label>
              <Input value={form.cognome} onChange={(e) => set("cognome", e.target.value)} className="mt-1.5 rounded-lg" />
            </div>
            <div className="md:col-span-2">
              <Label className="label-tag flex items-center gap-1"><MapPin size={12} /> Indirizzo sede legale</Label>
              <Input value={form.indirizzo} onChange={(e) => set("indirizzo", e.target.value)} placeholder="Via, piazza..." className="mt-1.5 rounded-lg" data-testid="form-indirizzo" />
              <div className="text-[10px] mt-1" style={{ color: "var(--ds-text-secondary)" }}>
                Suggerimento: aggiungi una Google Maps API key per attivare l'autocomplete completo.
              </div>
            </div>
            <div>
              <Label className="label-tag">Civico</Label>
              <Input value={form.civico} onChange={(e) => set("civico", e.target.value)} className="mt-1.5 rounded-lg" />
            </div>
            <div>
              <Label className="label-tag">CAP</Label>
              <Input value={form.cap} onChange={(e) => set("cap", e.target.value)} className="mt-1.5 rounded-lg" maxLength={5} />
            </div>
            <div>
              <Label className="label-tag">Città / Comune</Label>
              <Input list="cities-list" value={form.citta} onChange={(e) => set("citta", e.target.value)} className="mt-1.5 rounded-lg" />
              <datalist id="cities-list">
                {ITALIAN_CITIES.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            <div>
              <Label className="label-tag">Provincia</Label>
              <Select value={form.provincia} onValueChange={(v) => set("provincia", v)}>
                <SelectTrigger className="mt-1.5 rounded-lg" data-testid="form-provincia">
                  <SelectValue placeholder="Seleziona…" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {ITALIAN_PROVINCES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tag">Paese / Nazione</Label>
              <Select value={form.paese} onValueChange={(v) => set("paese", v)}>
                <SelectTrigger className="mt-1.5 rounded-lg">
                  <SelectValue placeholder="Italia" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {COUNTRIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="label-tag">Email *</Label>
              <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="mt-1.5 rounded-lg" required data-testid="form-email" />
            </div>
            <div>
              <Label className="label-tag">Telefono *</Label>
              <Input value={form.telefono} onChange={(e) => set("telefono", e.target.value)} className="mt-1.5 rounded-lg" required data-testid="form-telefono" />
            </div>
            <div>
              <Label className="label-tag">PEC</Label>
              <Input type="email" value={form.pec} onChange={(e) => set("pec", e.target.value)} className="mt-1.5 rounded-lg" />
            </div>
            <div>
              <Label className="label-tag">Codice Fiscale</Label>
              <Input value={form.codice_fiscale} onChange={(e) => set("codice_fiscale", e.target.value)} className="mt-1.5 rounded-lg" />
            </div>
            <div>
              <Label className="label-tag">Partita IVA</Label>
              <Input value={form.partita_iva} onChange={(e) => set("partita_iva", e.target.value)} className="mt-1.5 rounded-lg" data-testid="form-partita-iva" />
            </div>

            <DialogFooter className="md:col-span-2 mt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Annulla</Button>
              <Button type="submit" className="ds-btn-primary" style={{ background: "var(--ds-primary)" }} data-testid="fornitore-submit">
                {editing ? "Salva modifiche" : "Crea fornitore"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare il fornitore?</AlertDialogTitle>
            <AlertDialogDescription>Questa azione non può essere annullata.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} style={{ background: "var(--ds-danger)" }} data-testid="fornitore-confirm-delete">
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminare {selectedIds.size} fornitori?</AlertDialogTitle>
            <AlertDialogDescription>
              I prodotti associati manterranno i loro dati ma perderanno il riferimento al fornitore. Operazione irreversibile.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={bulkDelete} style={{ background: "var(--ds-danger)" }} data-testid="fornitori-bulk-confirm">
              Elimina tutti
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent className="max-w-2xl" data-testid="fornitori-import-dialog">
          <DialogHeader>
            <DialogTitle className="font-brand text-3xl font-light">Importa fornitori da XLS</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="text-sm" style={{ color: "var(--ds-text-secondary)" }}>
              Il file deve avere le colonne nell'intestazione (prima riga).
            </div>
            <div className="text-xs p-3 rounded-lg border" style={{ borderColor: "var(--ds-border)", color: "var(--ds-text-secondary)" }}>
              <b>Obbligatori:</b> ragione_sociale, nome, email, telefono
              <br />
              <b>Opzionali:</b> cognome, pec, codice_fiscale, partita_iva, indirizzo, civico, cap, citta, provincia, paese
            </div>

            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={downloadTemplate} className="rounded-lg" data-testid="fornitori-template-inline">
                <Download size={14} className="mr-1.5" /> Scarica template
              </Button>
              <label className="ds-btn-primary inline-flex items-center cursor-pointer rounded-lg px-4 py-2 text-sm" style={{ background: "var(--ds-primary)", color: "white" }}>
                <Upload size={14} className="mr-1.5" />
                {importBusy ? "Caricamento…" : "Scegli file .xlsx"}
                <input type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} disabled={importBusy} data-testid="fornitori-import-input" />
              </label>
            </div>

            {importResult && (
              <div className="space-y-2">
                <div className="text-sm">
                  <b style={{ color: "#7B8B6F" }}>Creati: {importResult.created}</b>
                  {" · "}
                  <b style={{ color: importResult.skipped?.length ? "var(--ds-danger)" : "inherit" }}>
                    Saltati: {importResult.skipped?.length || 0}
                  </b>
                  {" · "}
                  Totale righe: {importResult.total_rows}
                </div>
                {importResult.skipped?.length > 0 && (
                  <div className="max-h-48 overflow-y-auto text-xs p-3 rounded-lg border" style={{ borderColor: "var(--ds-border)" }}>
                    {importResult.skipped.map((s, i) => (
                      <div key={`imp-skip-${s.row || i}`} className="py-1 border-b last:border-b-0" style={{ borderColor: "var(--ds-border)" }}>
                        <b>Riga {s.row}:</b> {s.reason}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setImportOpen(false)}>Chiudi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
